import React, { useEffect, useState } from 'react';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { ROLES, ROLE_THEMES, ROLE_CTA_LABEL, scrollToSection } from './landingData';
import LandingSection from './LandingSection';
import LandingCard from './LandingCard';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '../ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

function RoleCard({ role }) {
  const theme = ROLE_THEMES[role.theme];
  const Icon = role.icon || ShieldCheck;
  return (
    <LandingCard className={`flex h-full flex-col p-5 ${theme.card}`}>
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 ring-1 ring-white/10">
          <Icon className="h-4 w-4 text-white/80" />
        </div>
        <p className="text-sm font-semibold text-white">{role.title}</p>
      </div>
      <ul className="mt-3 flex-1 space-y-2">
        {role.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-xs text-white/60">
            <CheckCircle2 className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${theme.bullet}`} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Button
        variant="outline"
        className={`mt-4 w-full bg-transparent ${theme.button}`}
        onClick={role.onViewPermissions}
      >
        {ROLE_CTA_LABEL}
      </Button>
    </LandingCard>
  );
}

function CarouselDots({ count, active }) {
  return (
    <div className="mt-4 flex justify-center gap-1.5 lg:hidden">
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className={`h-1.5 rounded-full transition-all ${
            i === active ? 'w-4 bg-blue-400' : 'w-1.5 bg-white/20'
          }`}
        />
      ))}
    </div>
  );
}

export default function LandingRoles() {
  const [api, setApi] = useState(null);
  const [active, setActive] = useState(0);
  const [dialogRole, setDialogRole] = useState(null);

  useEffect(() => {
    if (!api) return undefined;
    const onSelect = () => setActive(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
    return () => api.off('select', onSelect);
  }, [api]);

  return (
    <LandingSection
      id="roles"
      title="Role-based access"
      subtitle="Each role gets a clean, focused workspace with the right permissions—nothing more."
      innerClassName=""
    >

      <div className="mt-5 lg:hidden">
        <Carousel setApi={setApi} opts={{ align: 'start', loop: false }}>
          <CarouselContent className="-ml-3">
            {ROLES.map((role) => (
              <CarouselItem key={role.title} className="basis-[85%] pl-3 sm:basis-[70%]">
                <RoleCard role={{ ...role, onViewPermissions: () => setDialogRole(role) }} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <CarouselDots count={ROLES.length} active={active} />
      </div>

      <div className="mt-5 hidden gap-4 lg:grid lg:grid-cols-3">
        {ROLES.map((role) => (
          <RoleCard key={role.title} role={{ ...role, onViewPermissions: () => setDialogRole(role) }} />
        ))}
      </div>

      <Dialog open={!!dialogRole} onOpenChange={(open) => !open && setDialogRole(null)}>
        <DialogContent className="border-white/10 bg-[#050816] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {dialogRole?.title} permissions (overview)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-white/70">
            <p>
              This is a marketing overview. Actual access is enforced by role and your school’s configuration.
            </p>
            <ul className="space-y-2">
              {(dialogRole?.bullets || []).map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-blue-300" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="w-full border-white/20 bg-transparent text-white hover:bg-white/10"
              onClick={() => {
                setDialogRole(null);
                scrollToSection('contact');
              }}
            >
              Contact us for a demo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </LandingSection>
  );
}
